import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Lock } from 'lucide-react';
import { AccessDenied } from './AccessDenied';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roleRequired?: UserRole;
  fallback?: React.ReactNode;
  showAlert?: boolean;
}

/**
 * Componente para proteger rotas baseadas em roles
 * 
 * @param children - Conteúdo a ser renderizado se o usuário tiver permissão
 * @param roleRequired - Role mínimo necessário para acessar a rota
 * @param fallback - Componente alternativo a ser renderizado se não tiver permissão
 * @param showAlert - Se deve mostrar um alerta quando não tiver permissão
 */
export function ProtectedRoute({ 
  children, 
  roleRequired = 'viewer', 
  fallback,
  showAlert = true 
}: ProtectedRouteProps) {
  const { profile, loading, hasUserPermission, isTi } = useAuth();

  // Se ainda está carregando, mostrar loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Se não há usuário logado
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Alert className="max-w-md">
          <Lock className="h-4 w-4" />
          <AlertDescription>
            Você precisa estar logado para acessar esta página.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Se o usuário não tem a permissão necessária (TI sempre tem acesso)
  if (!isTi() && !hasUserPermission(roleRequired)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (showAlert) {
      // Determinar o nome do módulo baseado no contexto
      let moduleName = "esta funcionalidade";
      if (roleRequired === 'admin') {
        moduleName = "área administrativa";
      }

      return <AccessDenied requiredRole={roleRequired} moduleName={moduleName} />;
    }

    return null;
  }

  // Se tem permissão, renderizar o conteúdo
  return <>{children}</>;
}

/**
 * Componente para proteger conteúdo baseado em permissões específicas
 */
interface ProtectedContentProps {
  children: React.ReactNode;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  fallback?: React.ReactNode;
}

export function ProtectedContent({ 
  children, 
  canView = true, 
  canEdit = false, 
  canDelete = false,
  fallback 
}: ProtectedContentProps) {
  const { canView: userCanView, canEdit: userCanEdit, canDelete: userCanDelete } = useAuth();

  // Verificar se o usuário tem todas as permissões necessárias
  const hasPermission = 
    (!canView || userCanView()) && 
    (!canEdit || userCanEdit()) && 
    (!canDelete || userCanDelete());

  if (!hasPermission) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}

/**
 * Hook para verificar se o usuário pode executar uma ação
 */
export function usePermission() {
  const { profile, hasUserPermission, isTi } = useAuth();

  const can = {
    view: (roleRequired: UserRole = 'viewer') => isTi() || hasUserPermission(roleRequired),
    edit: (roleRequired: UserRole = 'operator') => isTi() || hasUserPermission(roleRequired),
    delete: (roleRequired: UserRole = 'admin') => isTi() || hasUserPermission(roleRequired),
    manageUsers: () => isTi() || hasUserPermission('admin'),
    manageSettings: () => isTi() || hasUserPermission('admin'),
    manageFinance: () => isTi() || hasUserPermission('operator')
  };

  return {
    profile,
    can,
    hasPermission: hasUserPermission,
    isTi
  };
} 